import { LitElement, type PropertyValueMap, html } from 'lit'
import { property, customElement } from 'lit/decorators.js'
import { type Ref, ref, createRef } from 'lit/directives/ref.js'
import '@ui5/webcomponents/dist/Dialog'
import style from './style/zaptic-modal-v2-style'
import { waitForDomElem } from '../lib/helpers'

export enum Events {
    onClose = 'onClose',
}

export interface CustomEvents {
    [Events.onClose]: CustomEvent<void>
}

@customElement('zaptic-modal-v2')
export class ModalV2 extends LitElement {
    static styles = style

    modalRef: Ref<HTMLElement> = createRef()

    @property({ type: Boolean, attribute: 'is-open' })
    isOpen: boolean = false

    private onBeforeClose = (event: CustomEvent<{ escPressed: boolean }>) => {
        if (event.detail.escPressed) {
            // Let the parent know the modal has been closed via the ESC key.
            const event = new CustomEvent(Events.onClose, { composed: true })

            this.dispatchEvent(event)
        }
    }

    protected willUpdate(_changedProperties: PropertyValueMap<ModalV2>): void {
        if (_changedProperties.has('isOpen') && this.isOpen === true) {
            /*
            Need to remove ui5-static-area in order not to cause errors in elm, and hasCustomOverlay makes it redundant.
            Don't forget to add class to host!
            */
            waitForDomElem('ui5-static-area', { childList: true }).then((area: any) => {
                if (this.shadowRoot.host.classList.contains('has-custom-overlay') && area) {
                    area.remove()
                }
            })
        }
    }

    firstUpdated() {
        this.modalRef.value?.addEventListener('before-close', this.onBeforeClose)
    }

    disconnectedCallback() {
        super.disconnectedCallback()

        this.modalRef.value?.removeEventListener('before-close', this.onBeforeClose)
    }

    render() {
        return html`
            <ui5-dialog
                ${ref(this.modalRef)}
                .open="${this.isOpen}"
                exportparts="content, header, footer"
                part="wrapper"
            >
                <div class="header" slot="header">
                    <slot name="header"></slot>
                </div>

                <slot name="content"></slot>

                <div class="footer" slot="footer">
                    <slot name="footer"></slot>
                </div>
            </ui5-dialog>
        `
    }
}
